import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Award, Plus, X } from 'lucide-react';
import { createSkill } from '@/services/staffService';
import { Badge } from '@/components/ui/badge';

interface AddSkillDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddSkillDialog({ isOpen, onClose, onSuccess }: AddSkillDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newCertName, setNewCertName] = useState('');
  const [isAddingCert, setIsAddingCert] = useState(false);
  const [processSearchTerm, setProcessSearchTerm] = useState('');
  const [showProcessSuggestions, setShowProcessSuggestions] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    requiredForProcesses: [] as string[],
    certificationOptions: [] as string[],
  });

  // Common business and compliance processes
  const availableProcesses = [
    'Compliance Audit',
    'Policy Review',
    'Incident Response',
    'Control Testing',
    'Regulatory Reporting',
    'Vendor Assessment',
    'Business Continuity Planning',
    'Data Privacy',
    'Security Testing',
    'Third Party Management',
    'Internal Audit',
    'External Audit',
    'SOX Compliance',
    'GDPR Compliance',
    'ISO 27001 Certification',
    'PCI DSS Compliance',
    'Penetration Testing',
    'Vulnerability Assessment',
    'Change Management',
    'Access Control Review',
    'Data Classification',
    'Disaster Recovery Testing',
    'Crisis Management',
    'Control Design',
    'Control Implementation',
    'Control Monitoring',
    'Threat Assessment',
    'Security Awareness Training',
    'Business Impact Analysis',
    'Process Documentation',
  ];

  // Filter processes based on search term
  const filteredProcesses = availableProcesses.filter(process => 
    process.toLowerCase().includes(processSearchTerm.toLowerCase()) &&
    !formData.requiredForProcesses.includes(process)
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await createSkill({
        name: formData.name,
        category: formData.category,
        description: formData.description,
        requiredForProcesses: formData.requiredForProcesses,
      });
      
      // Reset form
      setFormData({
        name: '',
        category: '',
        description: '',
        requiredForProcesses: [],
        certificationOptions: [],
      });
      
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add skill');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const removeProcess = (processName: string) => {
    handleChange('requiredForProcesses', formData.requiredForProcesses.filter(p => p !== processName));
  };

  const selectProcess = (processName: string) => {
    if (!formData.requiredForProcesses.includes(processName)) {
      handleChange('requiredForProcesses', [...formData.requiredForProcesses, processName]);
      setProcessSearchTerm('');
      setShowProcessSuggestions(false);
    }
  };

  const handleProcessInputChange = (value: string) => {
    setProcessSearchTerm(value);
    setShowProcessSuggestions(value.length > 0);
  };

  const handleProcessInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredProcesses.length > 0) {
      e.preventDefault();
      const firstProcess = filteredProcesses[0];
      if (firstProcess) {
        selectProcess(firstProcess);
      }
    } else if (e.key === 'Escape') {
      setShowProcessSuggestions(false);
      setProcessSearchTerm('');
    }
  };

  const startAddingCert = () => {
    setIsAddingCert(true);
    setNewCertName('');
  };

  const confirmAddCert = () => {
    if (newCertName.trim() && !formData.certificationOptions.includes(newCertName.trim())) {
      handleChange('certificationOptions', [...formData.certificationOptions, newCertName.trim()]);
      setNewCertName('');
      setIsAddingCert(false);
    }
  };

  const cancelAddCert = () => {
    setNewCertName('');
    setIsAddingCert(false);
  };

  const removeCertification = (certName: string) => {
    handleChange('certificationOptions', formData.certificationOptions.filter(c => c !== certName));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Add New Skill
          </DialogTitle>
          <DialogDescription>
            Add a new skill to the skills library with process requirements and certification recommendations.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-white border-b border-neutral-800 pb-2">Basic Information</h3>
              
              <div className="space-y-2">
                <Label htmlFor="name">Skill Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Policy Analysis"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-md text-white"
                  required
                >
                  <option value="">-- Select a category --</option>
                  <option value="Compliance">Compliance</option>
                  <option value="Technical">Technical</option>
                  <option value="Security">Security</option>
                  <option value="Data Analysis">Data Analysis</option>
                  <option value="Business Analysis">Business Analysis</option>
                  <option value="Project Management">Project Management</option>
                  <option value="Communication">Communication</option>
                  <option value="Leadership">Leadership</option>
                  <option value="Audit">Audit</option>
                  <option value="Finance">Finance</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe this skill, its applications, and key competencies..."
                  rows={4}
                />
              </div>
            </div>

            {/* Required for Processes */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
                <h3 className="text-sm font-semibold text-white">Required for Processes</h3>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Type to search and add processes:</Label>
                <div className="relative">
                  <Input
                    value={processSearchTerm}
                    onChange={(e) => handleProcessInputChange(e.target.value)}
                    onKeyDown={handleProcessInputKeyDown}
                    onFocus={() => processSearchTerm && setShowProcessSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowProcessSuggestions(false), 200)}
                    placeholder="Start typing process name..."
                    className="text-sm"
                  />
                  
                  {showProcessSuggestions && filteredProcesses.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-neutral-900 border border-neutral-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredProcesses.slice(0, 10).map((process) => (
                        <button
                          key={process}
                          type="button"
                          onClick={() => selectProcess(process)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-800 transition-colors"
                        >
                          {process}
                        </button>
                      ))}
                      {filteredProcesses.length > 10 && (
                        <div className="px-3 py-2 text-xs text-neutral-500 border-t border-neutral-800">
                          +{filteredProcesses.length - 10} more. Keep typing to filter...
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-neutral-500">
                  Press Enter to select the first match, Escape to clear
                </p>
              </div>

              {formData.requiredForProcesses.length > 0 && (
                <div className="p-3 bg-neutral-900 rounded-lg border border-neutral-800">
                  <Label className="text-xs text-neutral-400 mb-2 block">Selected Processes ({formData.requiredForProcesses.length}):</Label>
                  <div className="flex flex-wrap gap-2">
                    {formData.requiredForProcesses.map((process) => (
                      <Badge key={process} variant="outline" className="text-xs">
                        {process}
                        <button
                          type="button"
                          onClick={() => removeProcess(process)}
                          className="ml-2 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Recommended Certifications */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
                <h3 className="text-sm font-semibold text-white">Recommended Certifications (Optional)</h3>
                {!isAddingCert && (
                  <Button
                    type="button"
                    onClick={startAddingCert}
                    size="sm"
                    variant="outline"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Cert
                  </Button>
                )}
              </div>

              {isAddingCert && (
                <div className="p-3 bg-neutral-900 rounded-lg border border-neutral-800">
                  <Label className="text-xs mb-2 block">Certification Name</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newCertName}
                      onChange={(e) => setNewCertName(e.target.value)}
                      placeholder="e.g., CISSP, CISA, AWS Certified"
                      className="text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          confirmAddCert();
                        } else if (e.key === 'Escape') {
                          cancelAddCert();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={confirmAddCert}
                      size="sm"
                      disabled={!newCertName.trim()}
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      onClick={cancelAddCert}
                      size="sm"
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {formData.certificationOptions.length === 0 && !isAddingCert ? (
                <p className="text-sm text-neutral-500 italic">No certifications recommended yet</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {formData.certificationOptions.map((cert, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      <Award className="w-3 h-3 mr-1" />
                      {cert}
                      <button
                        type="button"
                        onClick={() => removeCertification(cert)}
                        className="ml-2 hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-neutral-500">
                Suggest certifications that validate expertise in this skill
              </p>
            </div>

            {/* Proficiency Guidelines */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-white border-b border-neutral-800 pb-2">Proficiency Levels</h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-2 bg-neutral-900 rounded border border-neutral-800">
                  <p className="font-semibold text-white mb-1">Beginner</p>
                  <p className="text-neutral-400">Basic understanding, requires supervision</p>
                </div>
                <div className="p-2 bg-neutral-900 rounded border border-neutral-800">
                  <p className="font-semibold text-white mb-1">Intermediate</p>
                  <p className="text-neutral-400">Can work independently on routine tasks</p>
                </div>
                <div className="p-2 bg-neutral-900 rounded border border-neutral-800">
                  <p className="font-semibold text-white mb-1">Advanced</p>
                  <p className="text-neutral-400">Expert level, can mentor others</p>
                </div>
                <div className="p-2 bg-neutral-900 rounded border border-neutral-800">
                  <p className="font-semibold text-white mb-1">Expert</p>
                  <p className="text-neutral-400">Subject matter expert, leads complex projects</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Skill'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
